import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { forkJoin, map, Observable, switchMap } from "rxjs";
import { environment } from "src/environments/environment";
import { WhiteLabelService } from "../util/white-label.service";
import { AuthService } from "./auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private oidcSecurityService = inject(OidcSecurityService);
  private authService = inject(AuthService);
  private whiteLabelService = inject(WhiteLabelService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      request.url.startsWith(environment.baseUrl) &&
      !request.url.startsWith(this.whiteLabelService.authUrl)
    ) {
      return forkJoin([
        this.oidcSecurityService.getAccessToken(),
        this.oidcSecurityService.getUserData(),
      ])
        .pipe(
          map((authData) => {
            // Token has expired, request will be rejected by the server
            if (authData[0] === null || authData[1] === null) {
              return next.handle(request);
            }

            let headers = request.headers;

            // Set headers from auth service
            headers = headers
              .append("Authorization", `Bearer ${authData[0]}`)
              .append("Domain", String(authData[1].domain))
              .append("Accept-Language", authData[1].language);

            // Set custom wcsw headers (access control headers)
            headers = this.addWcswState(headers);

            const updatedRequest = request.clone({ headers });
            return next.handle(updatedRequest);
          }),
        )
        .pipe(switchMap((c) => c)); // Flatten the map
    }

    // Auth related request handheld by oidc security lib
    return next.handle(request);
  }

  // Add wcsw state if wcsw options are available AND checkbox onlyMe is not checked
  addWcswState(headers: HttpHeaders): HttpHeaders {
    if (this.authService?.wcswState) {
      const wcswState = this.authService?.wcswState;
      const audit =
        this.booleanToWcswPart(wcswState.selectionIsPristine) +
        this.booleanToWcswPart(wcswState.onlyMe) +
        this.optionsToWcswPart(
          this.authService.wcswSelectedAuditOrganizations,
          wcswState.onlyMe,
        ) +
        this.optionsToWcswPart(
          this.authService.wcswSelectedAuditBranches,
          wcswState.onlyMe,
        ) +
        this.optionsToWcswPart(
          this.authService.wcswSelectedAuditDepartments,
          wcswState.onlyMe,
        ) +
        this.optionsToWcswPart(
          this.authService.wcswSelectedAuditPersons,
          wcswState.onlyMe,
        ) +
        this.optionsToWcswPart(
          this.authService.wcswSelectedAuditGroups,
          wcswState.onlyMe,
        );

      headers = headers.append("audit", audit);

      // Set inputter
      if (this.authService?.wcswState?.inputPerson) {
        headers = headers.append(
          "inputPersonId",
          this.authService.wcswState.inputPerson?.id.toString(),
        );
      }
    }

    return headers;
  }

  private booleanToWcswPart(value: boolean): string {
    return `${value ? "1" : "0"};`;
  }

  private optionsToWcswPart(options: number[], onlyMe: boolean): string {
    const compressedOptions = options.map((o) => o >>> 15);
    return `${onlyMe || options.length === 0 ? "0" : compressedOptions.toString()};`;
  }
}
